import { FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { IClaimFormStatus } from '../types/claim/form';
import { IManagementAPIClaimSubmissionResponse } from '../types/management-api/claims/submission/response';
import { IManagementAPIClaimUploadSignedUrlResponse } from '../types/management-api/claims/upload/response';
import { useApiClient } from '../services/api-client';
import { useAWSClient } from '../services/aws/client';
import { transformFileUploadRequestParams } from '../transformers/claims/upload/request/transformSignedUrlParams';
import { normalizeFilename } from '../utils/fileUtils';

export const useClaim = () => {
  const initialFormState = {
    isSuccessful: false,
    message: '',
    email: '',
    policyNumber: '',
    files: [],
  };
  const [formState, setFormState] =
    useState<IClaimFormStatus>(initialFormState);

  const apiClient = useApiClient();
  const awsClient = useAWSClient();
  const queryClient = useQueryClient();
  const getSignedUrl = useMutation({
    mutationFn: (): Promise<IManagementAPIClaimUploadSignedUrlResponse> =>
      apiClient.post(
        '/claim/upload/signed-url',
        transformFileUploadRequestParams(formState.files)
      ),
    onSuccess: (data: IManagementAPIClaimUploadSignedUrlResponse) => {
      queryClient.setQueryData(['signedUrls'], data);
    },
    onError: err => {
      console.log(err);
      setFormState({
        ...formState,
        isSuccessful: false,
        message: 'An error occurred with one or more of the uploaded files',
      });
    },
  });

  const sendFiles = useMutation({
    mutationFn: (params: { signedUrl: string; file: File }) =>
      awsClient.put(params.signedUrl, params.file),
    onSuccess: data => {
      queryClient.setQueryData(['aws'], data);
    },
    onError: err => {
      console.log(err);
    },
  });

  const sendClaim = useMutation({
    mutationFn: (
      batchId?: string | undefined
    ): Promise<IManagementAPIClaimSubmissionResponse> =>
      apiClient.post('/claim/submission', {
        email: formState.email,
        policyNumber: formState.policyNumber,
        ...(batchId && {
          upload: { batchId, count: formState.files.length },
        }),
      }),
    onSuccess: (data: IManagementAPIClaimSubmissionResponse) => {
      queryClient.setQueryData(['claim'], data);
    },
    onError: err => {
      console.log(err);
      setFormState({
        ...formState,
        isSuccessful: false,
        message:
          'An error occurred with the submission. Please try again or contact support.',
      });
    },
  });

  /**
   * Handler to deal with the form
   *
   * Steps:
   * 1. Transform filename - .toLowerCase(), replace space with underscore
   * 2. Get signed URLs
   * 3. Submit claim information
   * 4. Upload files
   *
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const signedUrls = await getSignedUrl.mutateAsync();
    await sendClaim.mutateAsync(signedUrls.id);
    await signedUrls.files.map(url => {
      formState.files.filter(file => {
        if (normalizeFilename(file.name) === url.name) {
          sendFiles.mutateAsync({ signedUrl: url.url, file: file });
        }
      });
    });

    setFormState({
      ...initialFormState,
      isSuccessful: true,
      message:
        'Thank you for submitting the claim. Our dedicated team will review the information and get back to you within working 10 days',
    });
  };

  return {
    formState,
    setFormState,
    handleSubmit,
  };
};
